import { Button, Flex, Input, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import Switcher from 'src/components/Switcher';
import PropTypes from 'prop-types';
import { TruncatedText } from '../common/Typography';

export default function PromptTextarea({
  title,
  placeholder,
  id,
  enabled,
  prompt,
  onSave,
  onReset,
  onCancel,
  onToggle,
  onPromptChange,
  isLoading,
  isSaving,
  maxLength,
  minRows,
  maxRows,
  isCollapsable,
  rightSideComponent
}) {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsable);
  const isResetVisible = onReset && (isCollapsable ? !isCollapsed : true);
  const isToggleVisible = onToggle && (isCollapsable ? !isCollapsed : true);

  useEffect(() => {
    if (!isSaving && isCollapsable) {
      setIsCollapsed(true);
    }
  }, [isSaving]);

  return (
    <Flex vertical>
      <Flex justify="space-between" align="center">
        <Typography.Title level={5} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        <Flex align="center" gap={16}>
          {isResetVisible && (
            <Button type="link" onClick={onReset}>
              Reset to Default
            </Button>
          )}
          {isToggleVisible && (
            <Flex gap={6}>
              <Switcher
                id={id}
                checked={enabled ?? false}
                onChange={onToggle}
                width={80}
                controlText={{ checked: 'ON', unchecked: 'OFF' }}
                isLoading={isLoading}
              />
            </Flex>
          )}
          {rightSideComponent}
        </Flex>
      </Flex>
      {isCollapsed ? (
        <Flex
          gap={16}
          align="center"
          justify="space-between"
          style={{
            marginTop: 10,
            borderRadius: 6,
            padding: '4px 11px',
            border: '1px solid #d9d9d9'
          }}
        >
          <span>
            <TruncatedText fontSize={14}>{prompt}</TruncatedText>
          </span>
          <Button type="link" onClick={() => setIsCollapsed(false)}>
            Edit
          </Button>
        </Flex>
      ) : (
        <Input.TextArea
          showCount
          value={prompt}
          onChange={onPromptChange}
          placeholder={placeholder}
          autoSize={{ minRows, maxRows }}
          style={{
            margin: '10px 0'
          }}
          maxLength={maxLength}
        />
      )}
      {!isCollapsed && (
        <Flex gap={10}>
          {isCollapsable && onCancel && (
            <Button
              onClick={() => {
                setIsCollapsed(true);
                onCancel();
              }}
            >
              Cancel
            </Button>
          )}
          {onSave && (
            <Button type="primary" loading={isSaving} onClick={onSave}>
              Save
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
}

PromptTextarea.propTypes = {
  title: PropTypes.string,
  enabled: PropTypes.bool,
  prompt: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  onSave: PropTypes.func,
  onReset: PropTypes.func,
  onCancel: PropTypes.func,
  onToggle: PropTypes.func,
  onPromptChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  maxLength: PropTypes.number,
  rightSideComponent: PropTypes.node,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  isCollapsable: PropTypes.bool
};

PromptTextarea.defaultProps = {
  title: '',
  enabled: false,
  prompt: '',
  id: 'prompt-textarea',
  placeholder: 'Write instructions here...',
  onSave: null,
  onReset: null,
  onCancel: null,
  onToggle: null,
  onPromptChange: null,
  isLoading: false,
  isSaving: false,
  maxLength: 1024,
  rightSideComponent: null,
  minRows: 3,
  maxRows: 6,
  isCollapsable: false
};
